import { track } from "@amplitude/analytics-react-native";
import { useApolloClient } from "@apollo/client";
import { GraphQLError } from "graphql";
import { useCallback, useMemo, useRef, useState } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import { amp } from "src/api/amplitude";
import RoundedButton from "src/components/RoundedButton";
import canNativeShare from "src/components/ShareMenu/helpers/canNativeShare";
import CommonPrefetchedData from "src/components/ShareMenu/helpers/PrefetchedData/CommonPrefetchedData";
import queuePrefetchData from "src/components/ShareMenu/helpers/queuePrefetchData";
import {
	isValidShareDownloadPayload,
	useShareDownload,
} from "src/components/ShareMenu/share-variants/ShareDownload";
import {
	isValidShareInstagramStoryPayload,
	useShareInstagramStory,
} from "src/components/ShareMenu/share-variants/ShareInstagramStory";
import { SharePayloadData } from "src/components/ShareMenu/types";
import log from "src/helpers/log";
import toastError from "src/helpers/toastError";
import IconDownloadPhoto from "src/icons/IconDownloadPhoto";
import IconFullInstagram from "src/icons/IconFullInstagram";
import { useInsideModal } from "src/modals";
import InternalModal from "src/modals/configs/InternalModal";
import { enumNever } from "src/shared/helpers/generalHelpers";
import useGlobalStore from "src/stores/useGlobalStore";
import { paddingLarge, paddingMedium, paddingSmall } from "src/styles/spacing";
import { Text } from "src/swsh-native";
import toast from "src/swsh-native/toast";
import { ShareableResolvedProps } from "../../hooks/useShareableResolvedProps";

const mockPayload: SharePayloadData = [
	{
		type: "Video",
		videoUrl: "mockUrl",
	},
];
const ShareableActions = ({
	style,
	onShareInitiated,
	onProcessingCompleted,
	resolvedProps,
}: {
	style?: StyleProp<ViewStyle>;
	onShareInitiated?: () => void;
	onProcessingCompleted?: () => void;
	resolvedProps: ShareableResolvedProps;
}) => {
	const {
		closeSelf,
		props: {
			title,
			subTitle,
			shareable: { composition, getDownloadUrl },
		},
	} = useInsideModal<InternalModal["Shareable"]>();
	const [height, setHeight] = useState<number | undefined>(undefined);
	const isIgAvailable = useGlobalStore((state) => state.isIgShareAvailable);
	const hasInstagramShare = useMemo(
		() => isValidShareInstagramStoryPayload(mockPayload, { isIgAvailable }),
		[isIgAvailable],
	);
	const hasDownloadShare = useMemo(() => isValidShareDownloadPayload(mockPayload), []);
	const prefetched = useRef<Record<string, CommonPrefetchedData>>({});
	const { handlePress: handleShareInstagramStory } = useShareInstagramStory({
		prefetched,
	});
	const { handlePress: handleShareDownload } = useShareDownload({
		prefetched,
	});

	const [loading, setLoading] = useState(false);
	const apolloClient = useApolloClient();
	const handleShare = useCallback(
		async (variant: "download" | "instagramStory") => {
			try {
				if (!resolvedProps || !resolvedProps.success) {
					return;
				}
				setLoading(true);
				onShareInitiated?.();
				const eventProperties = {
					composition,
				};
				track(amp.shareable[variant].requested, eventProperties);
				const downloadRes = await getDownloadUrl({
					apolloClient,
					context: resolvedProps.context,
					props: resolvedProps.props,
				});
				if (!downloadRes) {
					toast({
						preset: "error",
						title: "Error downloading your recap",
						message: "Please try again later",
					});
					closeSelf();
					return;
				}
				const { type, url } = downloadRes;
				const data = {
					type: "Video",
					videoUrl: url,
				} satisfies SharePayloadData[number];
				switch (type) {
					case "Video":
						queuePrefetchData(
							[
								{
									...data,
									successAmplitude: {
										eventName: amp.shareable[variant].completed,
										eventProperties,
									},
								},
							],
							prefetched.current,
						);
						break;
					default:
						return enumNever(type);
				}
				switch (variant) {
					case "download":
						if (canNativeShare) {
							closeSelf();
						}
						await handleShareDownload([data]);
						break;
					case "instagramStory":
						await handleShareInstagramStory([data]);
						break;
				}
				onProcessingCompleted?.();
			} catch (err) {
				if (!(err instanceof GraphQLError)) {
					toastError();
				}
				log.error({ err }, "Error sharing");
			} finally {
				setLoading(false);
			}
		},
		[
			apolloClient,
			closeSelf,
			composition,
			getDownloadUrl,
			handleShareDownload,
			handleShareInstagramStory,
			onProcessingCompleted,
			onShareInitiated,
			resolvedProps,
		],
	);
	const disabled = loading || !resolvedProps || !resolvedProps.success;

	return (
		<View
			style={[
				style,
				{
					width: "100%",
					alignItems: "center",
					minHeight: height,
				},
			]}
			onLayout={(event) => {
				const newHeight = event.nativeEvent.layout.height;
				if (height == null || newHeight > height) setHeight(newHeight);
			}}
		>
			{title ? (
				<Text
					semiLarge
					semibold
					style={{
						marginTop: paddingMedium,
						textAlign: "center",
						marginHorizontal: paddingMedium,
					}}
				>
					{title}
				</Text>
			) : null}
			{subTitle ? (
				<Text
					color="TextLight"
					style={{
						marginTop: paddingSmall,
						textAlign: "center",
						marginHorizontal: paddingMedium,
					}}
				>
					{subTitle}
				</Text>
			) : null}
			<View
				style={{
					marginTop: paddingLarge,
				}}
			>
				{hasInstagramShare && (
					<RoundedButton
						testID="RecapShareActions-Instagram"
						Icon={IconFullInstagram}
						colorVariant="Contrast"
						sizeVariant="Large"
						containerStyle={{
							marginTop: paddingMedium,
						}}
						onPress={() => handleShare("instagramStory")}
						disabled={disabled}
					>
						Share to Insta
					</RoundedButton>
				)}
				{hasDownloadShare && (
					<RoundedButton
						testID="RecapShareActions-Download"
						Icon={IconDownloadPhoto}
						colorVariant={hasInstagramShare ? "Match" : "Contrast"}
						sizeVariant="Large"
						containerStyle={{
							marginTop: paddingMedium,
						}}
						onPress={() => handleShare("download")}
						disabled={disabled}
					>
						Save to device
					</RoundedButton>
				)}
			</View>
		</View>
	);
};
export default ShareableActions;
